import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  Chip, 
  CircularProgress,
  IconButton,
  Avatar,
  Divider,
  Alert,
  TextField,
  Collapse,
  Button,
  Grid,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  CardActions
} from '@mui/material';
import { 
  ArrowUpward, 
  ArrowDownward, 
  Comment, 
  Visibility,
  ChatBubbleOutline,
  Send
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import supabaseClient from '../supabaseClient';
import Nav1 from '../components/Nav1';
import Footer from '../components/Footer';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ViewQuestion = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(true);
  const [answers, setAnswers] = useState([]);
  const [newAnswer, setNewAnswer] = useState('');
  const [showAnswerEditor, setShowAnswerEditor] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [latestQuestions, setLatestQuestions] = useState([]);
  const [commentCount, setCommentCount] = useState(0);

  useEffect(() => {
    console.log('Attempting to fetch question with ID:', id);
    fetchQuestionDetail();
    fetchComments();
    fetchAnswers();
    fetchLatestQuestions();
    fetchCommentCount();

    // Set up real-time subscription for comments
    const commentChannel = supabaseClient
      .channel('custom-channel-comments')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'comments2',
          filter: `question_id=eq.${id}`
        }, 
        (payload) => {
          console.log('Real-time comment update received:', payload);
          fetchComments(); // Refresh comments when changes occur
      })
      .subscribe((status) => {
        console.log('Comment subscription status:', status);
      });

    // Set up real-time subscription for answers
    const answerChannel = supabaseClient
      .channel('custom-channel-answers')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'answers2',
          filter: `question_id=eq.${id}`
        }, 
        (payload) => {
          console.log('Real-time answer update received:', payload);
          if (payload.eventType === 'INSERT') {
            // Add new answer to state
            const newAnswer = payload.new;
            setAnswers(prevAnswers => {
              // Check if answer already exists
              if (!prevAnswers.find(a => a.id === newAnswer.id)) {
                return [...prevAnswers, newAnswer];
              }
              return prevAnswers;
            });
          }
      })
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    // Cleanup subscription on unmount
    return () => {
      console.log('Cleaning up comment subscription');
      commentChannel.unsubscribe();
      console.log('Cleaning up subscription');
      answerChannel.unsubscribe();
    };
  }, [id]);

  useEffect(() => {
    console.log('Current answer content:', newAnswer);
  }, [newAnswer]);

  useEffect(() => {
    console.log('Current user:', user);
  }, [user]);

  const fetchQuestionDetail = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Log the query we're about to make
      console.log('Making Supabase query for ID:', id);
      
      const { data, error } = await supabaseClient
        .from('questions2')
        .select('*')
        .eq('id', id)
        .single();

      console.log('Supabase response:', { data, error });

      if (error) {
        setError(error.message);
        console.error('Supabase error:', error);
        return;
      }

      if (!data) {
        setError('Question not found');
        console.log('No data returned for ID:', id);
        return;
      }

      console.log('Successfully fetched question:', data);
      setQuestion(data);
    } catch (err) {
      console.error('Error in fetchQuestionDetail:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      // Fetch comments
      const { data: commentsData, error: commentsError } = await supabaseClient
        .from('comments2')
        .select(`
          id,
          content,
          created_at,
          user_id,
          question_id
        `)
        .eq('question_id', id)
        .order('created_at', { ascending: false });

      if (commentsError) throw commentsError;

      // Fetch profiles
      const userIds = [...new Set(commentsData.map(c => c.user_id))];
      const { data: profilesData, error: profilesError } = await supabaseClient
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Combine data
      const profilesMap = Object.fromEntries(
        profilesData.map(profile => [profile.id, profile])
      );

      const commentsWithProfiles = commentsData.map(comment => ({
        ...comment,
        profiles: profilesMap[comment.user_id] || null
      }));

      setComments(commentsWithProfiles);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchAnswers = async () => {
    try {
      console.log('Fetching answers for question:', id);
      
      // First fetch answers
      const { data: answersData, error: answersError } = await supabaseClient
        .from('answers2')
        .select('*')
        .eq('question_id', id)
        .order('created_at', { ascending: false });

      if (answersError) throw answersError;

      if (!answersData?.length) {
        setAnswers([]);
        return;
      }

      // Then fetch profiles for all answers
      const userIds = [...new Set(answersData.map(a => a.user_id))];
      const { data: profilesData, error: profilesError } = await supabaseClient
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Combine answers with profiles
      const profilesMap = Object.fromEntries(
        profilesData.map(profile => [profile.id, profile])
      );

      const answersWithProfiles = answersData.map(answer => ({
        ...answer,
        profiles: profilesMap[answer.user_id] || null
      }));

      console.log('Fetched answers:', answersWithProfiles);
      setAnswers(answersWithProfiles);

    } catch (error) {
      console.error('Error fetching answers:', error);
    }
  };

  const handleCommentSubmit = async () => {
    try {
      if (!user) {
        console.error('No user found');
        return;
      }

      setIsSubmittingComment(true);
      console.log('Submitting comment...');

      // Step 1: Insert the comment
      const { error: insertError } = await supabaseClient
        .from('comments2')
        .insert({
          content: newComment.trim(),
          question_id: id,
          user_id: user.id
        });

      if (insertError) throw insertError;

      // Step 2: Fetch the updated comments with user data
      const { data: commentsData, error: fetchError } = await supabaseClient
        .from('comments2')
        .select(`
          id,
          content,
          created_at,
          user_id,
          question_id
        `)
        .eq('question_id', id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Step 3: Fetch user profiles separately
      const userIds = [...new Set(commentsData.map(c => c.user_id))];
      const { data: profilesData, error: profilesError } = await supabaseClient
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Step 4: Combine comments with profiles
      const profilesMap = Object.fromEntries(
        profilesData.map(profile => [profile.id, profile])
      );

      const commentsWithProfiles = commentsData.map(comment => ({
        ...comment,
        profiles: profilesMap[comment.user_id] || null
      }));

      // Update state
      setComments(commentsWithProfiles);
      setNewComment('');

    } catch (error) {
      console.error('Error in handleCommentSubmit:', error);
      alert('Failed to submit comment. Please try again.');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleAnswerSubmit = async () => {
    try {
      if (!user || !newAnswer || newAnswer === '<p><br></p>') return;

      setIsSubmitting(true);

      // Insert answer
      const { error: insertError } = await supabaseClient
        .from('answers2')
        .insert({
          content: newAnswer,
          question_id: id,
          user_id: user.id
        });

      if (insertError) throw insertError;

      // Clear form and refresh answers immediately
      setNewAnswer('');
      setShowAnswerEditor(false);
      await fetchAnswers(); // Immediate refresh

    } catch (error) {
      console.error('Error submitting answer:', error);
      alert('Failed to submit answer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVote = async (direction) => {
    try {
      const newVotes = question.votes + (direction === 'up' ? 1 : -1);
      const { data, error } = await supabaseClient
        .from('questions2')
        .update({ votes: newVotes })
        .eq('id', id);

      if (error) throw error;
      setQuestion({ ...question, votes: newVotes });
    } catch (error) {
      console.error('Error updating votes:', error);
    }
  };

  const handleAnswerVote = async (answerId, direction) => {
    try {
      const answer = answers.find(a => a.id === answerId);
      const newVotes = answer.votes + (direction === 'up' ? 1 : -1);

      const { error } = await supabaseClient
        .from('answers2')
        .update({ votes: newVotes })
        .eq('id', answerId);

      if (error) throw error;

      setAnswers(prevAnswers =>
        prevAnswers.map(a =>
          a.id === answerId ? { ...a, votes: newVotes } : a
        )
      );
    } catch (error) {
      console.error('Error voting on answer:', error);
    }
  };

  // Add this useEffect to verify setup
  useEffect(() => {
    console.log('Component mounted with:', {
      questionId: id,
      userPresent: !!user,
      userId: user?.id,
    });
  }, []);

  // Set up real-time subscription
  useEffect(() => {
    fetchComments(); // Initial fetch

    // Set up real-time subscription
    const channel = supabaseClient
      .channel('comments-channel')
      .on('postgres_changes', 
        { 
          event: 'INSERT',
          schema: 'public',
          table: 'comments2',
          filter: `question_id=eq.${id}`
        },
        async (payload) => {
          console.log('New comment received:', payload);
          
          // Fetch the complete comment data including profile
          const { data, error } = await supabaseClient
            .from('comments2')
            .select(`
              *,
              profiles (
                username,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (!error && data) {
            setComments(prevComments => [data, ...prevComments]);
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    return () => {
      channel.unsubscribe();
    };
  }, [id]);

  // Add debug logging for user and question ID
  useEffect(() => {
    console.log('Current state:', {
      userId: user?.id,
      questionId: id,
      isAuthenticated: !!user
    });
  }, [user, id]);

  // Add debug logging for component state
  useEffect(() => {
    console.log('Component state:', {
      isAuthenticated: !!user,
      userId: user?.id,
      questionId: id,
      commentsCount: comments.length,
      isSubmitting: isSubmittingComment
    });
  }, [user, id, comments, isSubmittingComment]);

  // Add debug logging for Supabase connection
  useEffect(() => {
    const checkSupabaseConnection = async () => {
      try {
        const { data, error } = await supabaseClient
          .from('comments2')
          .select('count')
          .limit(1);
        
        console.log('Supabase connection test:', {
          success: !error,
          error: error,
          data: data
        });
      } catch (error) {
        console.error('Supabase connection error:', error);
      }
    };

    checkSupabaseConnection();
  }, []);

  // Add this useEffect to fetch answers when the component mounts
  useEffect(() => {
    fetchAnswers();
  }, [id]);

  // Set up real-time subscription
  useEffect(() => {
    fetchAnswers(); // Initial fetch

    // Subscribe to changes
    const channel = supabaseClient
      .channel('custom-all-channel')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all changes
          schema: 'public',
          table: 'answers2',
          filter: `question_id=eq.${id}`
        },
        (payload) => {
          console.log('Received real-time update:', payload);
          fetchAnswers(); // Refresh answers when changes occur
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    return () => {
      channel.unsubscribe();
    };
  }, [id]);

  // Debug logging
  useEffect(() => {
    console.log('Current answers state:', answers);
  }, [answers]);

  const fetchLatestQuestions = async () => {
    try {
      console.log('Fetching latest questions...');
      
      const { data, error } = await supabaseClient
        .from('questions2')
        .select(`
          id,
          title,
          created_at,
          user_id,
          profiles!questions2_user_id_fkey (
            username,
            avatar_url
          ),
          answers2 (count)
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('Error fetching latest questions:', error);
        throw error;
      }

      console.log('Latest questions data:', data);
      setLatestQuestions(data || []);
    } catch (error) {
      console.error('Failed to fetch latest questions:', error);
    }
  };

  // Add this useEffect for debugging
  useEffect(() => {
    console.log('Current latestQuestions state:', latestQuestions);
  }, [latestQuestions]);

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchLatestQuestions();
  }, []);

  // Add function to fetch comment count
  const fetchCommentCount = async () => {
    try {
      const { count, error } = await supabaseClient
        .from('comments2')
        .select('*', { count: 'exact' })
        .eq('question_id', id);

      if (error) throw error;
      setCommentCount(count || 0);
    } catch (error) {
      console.error('Error fetching comment count:', error);
    }
  };

  // Update useEffect to fetch comment count
  useEffect(() => {
    if (id) {
      fetchCommentCount();

      // Subscribe to comments changes
      const channel = supabaseClient
        .channel('comments-channel')
        .on('postgres_changes', 
          { 
            event: '*',
            schema: 'public',
            table: 'comments2',
            filter: `question_id=eq.${id}`
          },
          () => {
            fetchCommentCount(); // Refresh count on any changes
          }
        )
        .subscribe();

      return () => {
        channel.unsubscribe();
      };
    }
  }, [id]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Nav1 />
      <Container maxWidth="xl" sx={{ py: 4, flex: 1 }}>
        <Grid container spacing={3}>
          {/* Main content */}
          <Grid item xs={12} md={8}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box sx={{ p: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Debug Information:
                </Typography>
                <Typography variant="body2" component="pre" sx={{ 
                  backgroundColor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1
                }}>
                  Question ID: {id}
                </Typography>
              </Box>
            ) : question ? (
              <Box>
                {/* Question Card */}
                <Card sx={{ p: 4, mb: 3 }}>
                  <Box sx={{ display: 'flex', gap: 3 }}>
                    {/* Voting Section */}
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center'
                    }}>
                      <IconButton onClick={() => handleVote('up')}>
                        <ArrowUpward />
                      </IconButton>
                      <Typography variant="h6">{question.votes || 0}</Typography>
                      <IconButton onClick={() => handleVote('down')}>
                        <ArrowDownward />
                      </IconButton>
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{ mb: 3 }}>
                        {question.title}
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          mb: 3,
                          '& .ql-editor': {
                            padding: 0,
                          }
                        }}
                        dangerouslySetInnerHTML={{ __html: question.content }}
                      />

                      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                        {Array.isArray(question.tags) && question.tags.map((tag, index) => (
                          <Chip 
                            key={index} 
                            label={tag} 
                            size="small"
                            sx={{ 
                              backgroundColor: '#f0f7ff',
                              '&:hover': {
                                backgroundColor: '#e3f2fd'
                              }
                            }}
                          />
                        ))}
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Question Stats */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar 
                            src={question.profiles?.avatar_url} 
                            alt={question.profiles?.username}
                          />
                          <Box>
                            <Typography variant="subtitle2">
                              {question.profiles?.username || 'Anonymous'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Asked on {new Date(question.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Comment fontSize="small" />
                            <Typography variant="body2">
                              {commentCount} Comments
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Visibility sx={{ fontSize: 18, mr: 0.5 }} />
                            <Typography variant="body2">
                              {question.views || 0}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Comments Section */}
                  <Box sx={{ mt: 3 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        cursor: 'pointer',
                        mb: 2
                      }}
                      onClick={() => setShowComments(!showComments)}
                    >
                      <ChatBubbleOutline sx={{ fontSize: 20 }} />
                      <Typography variant="h6">
                        {comments.length} Comments
                      </Typography>
                    </Box>

                    <Collapse in={showComments}>
                      {/* Comments List */}
                      {comments.map((comment) => (
                        <Box 
                          key={comment.id} 
                          sx={{ 
                            display: 'flex', 
                            gap: 2, 
                            mb: 2,
                            pb: 2,
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                          }}
                        >
                          <Avatar 
                            src={comment.profiles?.avatar_url} 
                            sx={{ width: 32, height: 32 }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2">
                                {comment.profiles?.username || 'Anonymous'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(comment.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                            <Typography variant="body2">
                              {comment.content}
                            </Typography>
                          </Box>
                        </Box>
                      ))}

                      {/* Comment Input */}
                      {user ? (
                        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                          <Avatar 
                            src={user.user_metadata?.avatar_url} 
                            sx={{ width: 32, height: 32 }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="Add a comment..."
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  handleCommentSubmit();
                                }
                              }}
                              disabled={isSubmittingComment}
                              sx={{ mb: 1 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <IconButton 
                                onClick={handleCommentSubmit}
                                disabled={isSubmittingComment || !newComment.trim()}
                                sx={{ 
                                  color: '#108a00',
                                  '&:disabled': {
                                    color: 'rgba(0, 0, 0, 0.26)'
                                  }
                                }}
                              >
                                <Send />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          Please sign in to add a comment
                        </Alert>
                      )}
                    </Collapse>
                  </Box>
                </Card>

                {/* Answers Section */}
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h5" gutterBottom>
                    {answers.length} Answers
                  </Typography>

                  {/* Display answers */}
                  {answers.map((answer) => (
                    <Card 
                      key={answer.id} 
                      sx={{ 
                        mb: 3,
                        p: 3,
                        backgroundColor: '#f8f9fa',
                        borderRadius: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.12)',
                          transform: 'translateY(-1px)',
                          transition: 'all 0.3s ease'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 3 }}>
                        {/* Voting Section */}
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          minWidth: '50px'
                        }}>
                          <IconButton 
                            onClick={() => handleAnswerVote(answer.id, 'up')}
                            sx={{ color: '#108a00' }}
                          >
                            <ArrowUpward />
                          </IconButton>
                          <Typography variant="h6">{answer.votes || 0}</Typography>
                          <IconButton 
                            onClick={() => handleAnswerVote(answer.id, 'down')}
                            sx={{ color: '#d32f2f' }}
                          >
                            <ArrowDownward />
                          </IconButton>
                        </Box>

                        {/* Content Section */}
                        <Box sx={{ flex: 1 }}>
                          <Box 
                            sx={{ 
                              mb: 3,
                              '& .ql-editor': {
                                padding: 0,
                              },
                              '& pre': {
                                backgroundColor: '#f1f3f5',
                                padding: 2,
                                borderRadius: 1,
                                overflowX: 'auto'
                              },
                              '& code': {
                                fontFamily: 'monospace',
                                backgroundColor: '#f1f3f5',
                                padding: '2px 4px',
                                borderRadius: '4px'
                              }
                            }}
                            dangerouslySetInnerHTML={{ __html: answer.content }}
                          />

                          {/* User Info Section */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              backgroundColor: '#fff',
                              p: 2,
                              borderRadius: 1,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                            }}
                          >
                            <Avatar 
                              src={answer.profiles?.avatar_url} 
                              sx={{ width: 40, height: 40 }}
                            />
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                {answer.profiles?.username || 'Anonymous'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                answered {new Date(answer.created_at).toLocaleDateString()} at{' '}
                                {new Date(answer.created_at).toLocaleTimeString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  ))}

                  {/* Answer Editor */}
                  {user ? (
                    <Box sx={{ mt: 3 }}>
                      {!showAnswerEditor ? (
                        <Button
                          variant="contained"
                          onClick={() => setShowAnswerEditor(true)}
                          sx={{
                            backgroundColor: '#108a00',
                            '&:hover': { backgroundColor: '#0c6b00' },
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        >
                          Add Answer
                        </Button>
                      ) : (
                        <Card sx={{ p: 3, backgroundColor: '#fff' }}>
                          <ReactQuill
                            value={newAnswer}
                            onChange={setNewAnswer}
                            placeholder="Write your answer here..."
                            modules={{
                              toolbar: [
                                [{ 'header': [1, 2, 3, false] }],
                                ['bold', 'italic', 'underline', 'strike'],
                                [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                                ['link', 'code-block'],
                                ['clean']
                              ]
                            }}
                          />
                          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            <Button
                              variant="contained"
                              onClick={handleAnswerSubmit}
                              disabled={isSubmitting || !newAnswer || newAnswer === '<p><br></p>'}
                              sx={{
                                backgroundColor: '#108a00',
                                '&:hover': { backgroundColor: '#0c6b00' }
                              }}
                            >
                              {isSubmitting ? 'Posting...' : 'Post Answer'}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setShowAnswerEditor(false);
                                setNewAnswer('');
                              }}
                              disabled={isSubmitting}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Card>
                      )}
                    </Box>
                  ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Please sign in to add an answer
                    </Alert>
                  )}
                </Box>
              </Box>
            ) : (
              <Alert severity="warning">
                No question found with ID: {id}
              </Alert>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                position: { md: 'sticky' },
                top: '24px',
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
              }}
            >
              <CardHeader
                title="Latest Questions"
                sx={{
                  backgroundColor: '#108a00',
                  color: 'white',
                  '& .MuiCardHeader-title': {
                    fontSize: '1.1rem',
                    fontWeight: 500
                  }
                }}
              />
              <List sx={{ p: 0 }}>
                {latestQuestions.map((q) => (
                  <ListItem
                    key={q.id}
                    component={Link}
                    to={`/question/${q.id}`}
                    sx={{
                      borderBottom: '1px solid rgba(0,0,0,0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.02)',
                      },
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar src={q.profiles?.avatar_url} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle1"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: '1.3em',
                            maxHeight: '2.6em'
                          }}
                        >
                          {q.title}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            by {q.profiles?.username || 'Anonymous'}
                          </Typography>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              backgroundColor: '#e3f2fd',
                              borderRadius: '12px',
                              px: 1,
                              py: 0.25
                            }}
                          >
                            <Typography variant="caption">
                              {q.answers2?.[0]?.count || 0} answers
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
              <CardActions sx={{ justifyContent: 'center', p: 2 }}>
                <Button
                  component={Link}
                  to="/questions"
                  size="small"
                  sx={{
                    color: '#108a00',
                    '&:hover': {
                      backgroundColor: 'rgba(16,138,0,0.08)'
                    }
                  }}
                >
                  View All Questions
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </Box>
  );
};

export default ViewQuestion; 